import React, {useState} from "react";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import * as yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {yupResolver} from "@hookform/resolvers/yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import {useAuth} from "hooks/useAuth";
import {useServerErrors} from "hooks/useServerErrors";
import {APIPostFeedback} from "features/assistance/api/feedback";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import FormField from "shared/form/FormField";
import UploadButton from "shared/form/UploadButton";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import CustomButton from "shared/ui/CustomButton";

interface IFormFeedback {
    subject: string;
    message: string;
    phone: string;
    file?: File | null;
}

const schema = yup
    .object({})
    .required();

const SUCCESS_NOTIFICATION = {
    show: true,
    title: "Mesajul a fost trimis cu succes!",
    message: "Un reprezentant Brio îți va răspunde în cel mai scurt timp posibil.",
    icon: "fat envelope",
    closable: true,
    button: "ÎNCHIDE",
};

const Feedback = () => {
    const {update} = useAuth();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileUrl, setFileUrl] = useState<string>("");

    const methods = useForm<IFormFeedback>({
        resolver: yupResolver(schema),
    });

    const { handleServerErrors } = useServerErrors<IFormFeedback>(methods.setError);

    const updateUploadedFile = (file: File | null, selectedFileUrl: string) => {
        setSelectedFile(file);
        setFileUrl(selectedFileUrl);
    };

    const handleFileDelete = (e: React.MouseEvent) => {
        setSelectedFile(null);
    };

    const onSubmit: SubmitHandler<IFormFeedback> = (data) => {
        const formData = new FormData();
        formData.append("subject", data.subject);
        formData.append("phone", data.phone);
        formData.append("content", data.message);

        if (selectedFile instanceof File) {
            formData.append("file", selectedFile);
        }

        APIPostFeedback(formData)
            .then((response) => {
                if (response.data.success) {
                    // console.log("postFeedback OK", response);
                    update("error", SUCCESS_NOTIFICATION);
                    methods.reset({subject: "", message: "", phone: "",});
                    setSelectedFile(null);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                // console.log("postFeedback ERR", err);
                if (err?.response?.status === 400) {
                    handleServerErrors(err.response.data.data, { content: "message" });
                    if (err.response.data.data.file) setSelectedFile(null);
                } else {
                    update("error", {show: true, code: "A001"});
                }
            });
    };

    return (
        <Box component="main">
            <PageTitle>Feedback</PageTitle>

            <BoxContent>
                <Box width="100%">
                    <FormProvider {...methods}>
                        <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                            <Grid container spacing={1.5} alignItems="center">
                                <FormLabelBox title="Subiect:"/>
                                <FormFieldBox>
                                    <FormField
                                        name="subject"
                                        type="text"
                                        label="Subiect"
                                        defaultValue=""
                                        autoFocus={true}
                                        errorMessage={methods.formState.errors.subject?.message}
                                        error={!!methods.formState.errors.subject?.message}
                                    />
                                </FormFieldBox>

                                <FormLabelBox title="Mesaj:"/>
                                <FormFieldBox>
                                    <FormField
                                        name="message"
                                        type="textarea"
                                        defaultValue=""
                                        label="Mesaj"
                                        rows={4}
                                        errorMessage={methods.formState.errors.message?.message}
                                        error={!!methods.formState.errors.message?.message}
                                    />
                                </FormFieldBox>

                                <FormLabelBox title="Telefon:"/>
                                <FormFieldBox>
                                    <FormField
                                        name="phone"
                                        type="text"
                                        label="Telefon"
                                        defaultValue=""
                                        errorMessage={methods.formState.errors.phone?.message}
                                        error={!!methods.formState.errors.phone?.message}
                                    />
                                </FormFieldBox>

                                <FormLabelBox title="Imagine:"/>
                                <FormFieldBox>
                                    <>
                                        <UploadButton
                                            name="file"
                                            label="Imagine"
                                            defaultValue=""
                                            updateFileCb={updateUploadedFile}
                                            errorMessage={methods.formState.errors.file?.message}
                                            error={!!methods.formState.errors.file?.message}
                                            fileType="image"
                                        />
                                    </>
                                </FormFieldBox>
                                {fileUrl !== "" && selectedFile && (
                                    <>
                                        <FormLabelBox/>
                                        <FormFieldBox>
                                            <Badge
                                                sx={{backgroundColor: "transparent"}}
                                                badgeContent={
                                                    <IconButton aria-label="delete" onClick={handleFileDelete}>
                                                        <FontAwesomeIcon style={{fontSize: "1rem", color: "#9D0000"}}
                                                                         icon={"xmark"}/>
                                                    </IconButton>
                                                }
                                            >
                                                <img src={fileUrl} alt="" height="auto" width="200px"/>
                                            </Badge>
                                        </FormFieldBox>
                                    </>
                                )}
                                <FormLabelBox/>
                                <FormFieldBox>
                                    <CustomButton buttonType="textIcon" bgcolor="dark" submit={true}>
                                        TRIMITE FEEDBACK
                                    </CustomButton>
                                </FormFieldBox>
                            </Grid>
                        </form>
                    </FormProvider>
                </Box>
            </BoxContent>
        </Box>
    );
};

export default Feedback;
